// ./src/components/features/Tasks/TaskProgress/TaskProgress.tsx
//
// The execution controls for a single task: its lifecycle status badge, the tracked
// time (live-ticking while running), and the start / pause / complete / reopen
// buttons. Shared by the tasks list rows and the task edit drawer. Time is
// server-authoritative; this only DISPLAYS the running segment locally and asks the
// server to transition — the returned row is the source of truth.

import React, { useEffect, useState } from "react";
import { Badge, Button, Group, Text, Tooltip } from "@mantine/core";

import { Task, TASK_STATUS, TASK_TRANSITION, TaskTransitionAction } from "@/utils/api";
import { AppIcon } from "@/components/generic";

import {
  deriveElapsedSec,
  formatDurationMin,
  formatDurationSec,
  getTaskStatusColor,
  getTaskStatusLabel,
} from "../taskHelpers";

export interface TaskProgressProps {
  task: Task;
  onTransition: (action: TaskTransitionAction) => void;
  transitioning?: boolean;
  /** Compact = list rows (icon-only buttons); full = drawer panel (labelled buttons). */
  compact?: boolean;
  disabled?: boolean;
}

// Re-render every second only while a task is actively running, so the elapsed
// time ticks without a global timer.
const useTick = (active: boolean): number => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) return;

    const id = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(id);
  }, [active]);

  return now;
};

export const TaskProgress: React.FC<TaskProgressProps> = ({
  task,
  onTransition,
  transitioning = false,
  compact = false,
  disabled = false,
}) => {
  const status = task.taskStatus || TASK_STATUS.TODO;
  const running = status === TASK_STATUS.IN_PROGRESS;
  const now = useTick(running);
  const elapsed = deriveElapsedSec(task, now);

  const busy = transitioning || disabled;

  const btnSize = compact ? "compact-xs" : "xs";

  const startBtn = (label: string) => (
    <Button
      size={btnSize}
      variant="light"
      color="blue"
      leftSection={<AppIcon iconName="PlayerPlay" size={14} />}
      onClick={() => onTransition(TASK_TRANSITION.START)}
      loading={transitioning}
      disabled={busy}
    >
      {label}
    </Button>
  );

  const pauseBtn = (
    <Button
      size={btnSize}
      variant="light"
      color="yellow"
      leftSection={<AppIcon iconName="PlayerPause" size={14} />}
      onClick={() => onTransition(TASK_TRANSITION.PAUSE)}
      loading={transitioning}
      disabled={busy}
    >
      Pause
    </Button>
  );

  const completeBtn = (
    <Button
      size={btnSize}
      variant="light"
      color="green"
      leftSection={<AppIcon iconName="Check" size={14} />}
      onClick={() => onTransition(TASK_TRANSITION.COMPLETE)}
      loading={transitioning}
      disabled={busy}
    >
      Complete
    </Button>
  );

  const reopenBtn = (
    <Button
      size={btnSize}
      variant="subtle"
      color="gray"
      leftSection={<AppIcon iconName="RotateClockwise" size={14} />}
      onClick={() => onTransition(TASK_TRANSITION.REOPEN)}
      loading={transitioning}
      disabled={busy}
    >
      Reopen
    </Button>
  );

  const controls = (() => {
    switch (status) {
      case TASK_STATUS.TODO:
        return (
          <>
            {startBtn("Start")}
            {completeBtn}
          </>
        );
      case TASK_STATUS.IN_PROGRESS:
        return (
          <>
            {pauseBtn}
            {completeBtn}
          </>
        );
      case TASK_STATUS.PAUSED:
        return (
          <>
            {startBtn("Resume")}
            {completeBtn}
          </>
        );
      case TASK_STATUS.DONE:
        return reopenBtn;
      default:
        return null;
    }
  })();

  const expected = task.expectedDurationMin ? formatDurationMin(task.expectedDurationMin) : null;

  return (
    <Group gap="xs" wrap="wrap" align="center">
      <Badge color={getTaskStatusColor(status)} variant={status === TASK_STATUS.DONE ? "filled" : "light"} size="sm">
        {getTaskStatusLabel(status)}
      </Badge>

      {(elapsed > 0 || running || expected) && (
        <Tooltip label={expected ? `Tracked ${formatDurationSec(elapsed)} of ${expected} planned` : "Tracked time"}>
          <Group gap={4} align="center">
            <AppIcon iconName="Clock" size={14} />
            <Text fz="md" fw={500} c={running ? "blue" : undefined}>
              {formatDurationSec(elapsed)}
              {expected ? <Text component="span" fz="sm" c="dimmed">{` / ${expected}`}</Text> : null}
            </Text>
          </Group>
        </Tooltip>
      )}

      <Group gap={6}>{controls}</Group>
    </Group>
  );
};

export default TaskProgress;
